import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { 
  CheckCircle, Mail, User, Shield, Loader2, 
  AlertCircle, ArrowRight, Lock
} from "lucide-react";

export const AcceptInvitePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    if (token) {
      fetchInviteDetails();
    } else {
      setError("No invitation token provided");
      setLoading(false);
    }
  }, [token]);

  const fetchInviteDetails = async () => {
    try {
      const response = await fetch(`${API}/invite/${token}`);
      if (response.ok) {
        const data = await response.json();
        setInviteData(data);
      } else {
        const err = await response.json();
        setError(err.detail || "Invalid or expired invitation");
      }
    } catch (_err) {
      setError("Unable to verify invitation");
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/invite/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success("Account created successfully!");
        
        // Auto-login with the returned token
        if (data.token) {
          localStorage.setItem("token", data.token);
          await login(data.user.email, password);
        }
        
        navigate("/onboarding");
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to create account");
      }
    } catch (_err) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const getTierLabel = (tier) => {
    switch(tier) {
      case "basic": return "Basic Consulting";
      case "premium_rapid": return "R.A.P.I.D.™ Program";
      case "growth_strategic": return "Strategic Foundation";
      case "growth_optimization": return "Growth & Optimization";
      case "growth_scale": return "Scale & Leadership";
      default: return "Basic Consulting";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-white">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-white p-4">
        <Card className="max-w-md w-full border-red-200">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-neutral-900 mb-2">Invitation Error</h2>
            <p className="text-neutral-600 mb-6">{error}</p>
            <div className="space-y-3">
              <Button onClick={() => navigate("/login")} className="w-full bg-emerald-600 hover:bg-emerald-700">
                Go to Login
              </Button>
              <Button variant="outline" onClick={() => navigate("/")} className="w-full">
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-white p-4" data-testid="accept-invite-page">
      <div className="w-full max-w-md">
        {/* Logo Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-xl">MB</span>
          </div>
          <h1 className="text-2xl font-bold text-neutral-900">My Biz Consulting</h1>
          <p className="text-neutral-500">Client Success Platform</p>
        </div>

        <Card className="border-emerald-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Mail className="w-6 h-6 text-emerald-600" />
            </div>
            <CardTitle className="text-xl">You're Invited!</CardTitle>
            <CardDescription>
              {inviteData?.invited_by_name} has invited you to join
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Invite Details */}
            <div className="bg-neutral-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-neutral-400" />
                <div>
                  <p className="text-xs text-neutral-500">Your Name</p>
                  <p className="font-medium text-neutral-900">{inviteData?.name}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-neutral-400" />
                <div>
                  <p className="text-xs text-neutral-500">Email</p>
                  <p className="font-medium text-neutral-900">{inviteData?.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Shield className="w-5 h-5 text-neutral-400" />
                <div>
                  <p className="text-xs text-neutral-500">Your Tier</p>
                  <Badge className="bg-emerald-100 text-emerald-700 mt-1">
                    {getTierLabel(inviteData?.tier)}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Password Form */}
            <form onSubmit={handleAcceptInvite} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Create Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="At least 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
                disabled={submitting}
                data-testid="accept-invite-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Accept & Create Account
                  </>
                )}
              </Button>
            </form>

            <p className="text-xs text-center text-neutral-500">
              By accepting, you agree to our Terms of Service and Privacy Policy
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-neutral-500 mt-6">
          Already have an account?{" "}
          <button onClick={() => navigate("/login")} className="text-emerald-600 hover:underline font-medium">
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
